import classNames from 'classnames'

import { DateService } from 'common/services/dateService'

import type {
  TeamMember,
  TeamRequest,
  TeamRequestAction,
} from 'features/TeamChat/api/teamChat.types'
import { TEAM_COPY } from 'features/TeamChat/teamChat.constants'
import styles from 'features/TeamChat/teamChat.module.scss'
import { formatTeamStamp } from 'features/TeamChat/teamChat.time'

import { MemberAvatar } from '../MemberAvatar'
import { TeamButton } from '../TeamButton'
import { CheckIcon } from '../TeamIcons'
import { MentionText } from '../MentionText'

type Props = {
  request: TeamRequest
  fresh?: boolean
  members: TeamMember[]
  timeZone: string
  busy: boolean
  onAction: (id: number, action: TeamRequestAction) => void
}

const nowIso = (timeZone: string) => DateService.getCurrentTimeInTimezone(timeZone).toISOString()

const CHIP_CLASS = {
  open: styles.chipOpen,
  in_progress: styles.chipProgress,
  done: styles.chipDone,
}

const Actions = ({ request, busy, onAction }: Omit<Props, 'members' | 'timeZone'>) => {
  const act = (action: TeamRequestAction) => () => onAction(request.id, action)
  if (request.status === 'open')
    return (
      <TeamButton loading={busy} disabled={busy} onClick={act('claim')}>
        {TEAM_COPY.CLAIM}
      </TeamButton>
    )
  if (request.status === 'done')
    return request.owner ? (
      <div className={styles.doneBy}>
        <CheckIcon />
        <span>{TEAM_COPY.DONE_BY(request.owner.name)}</span>
      </div>
    ) : null
  if (!request.owned_by_me)
    return request.owner ? (
      <div className={styles.owner}>
        <MemberAvatar user={request.owner} />
        <span>{TEAM_COPY.ON_IT(request.owner.name)}</span>
      </div>
    ) : null
  return (
    <>
      <div className={styles.youOnIt}>{TEAM_COPY.YOU_ON_IT}</div>
      <div className={styles.row}>
        <TeamButton grow disabled={busy} onClick={act('done')}>
          {TEAM_COPY.MARK_DONE}
        </TeamButton>
        <TeamButton variant='secondary' disabled={busy} onClick={act('release')}>
          {TEAM_COPY.RELEASE}
        </TeamButton>
      </div>
    </>
  )
}

export const RequestCard = ({ request, fresh, members, timeZone, busy, onAction }: Props) => (
  <article
    className={classNames(styles.card, {
      [styles.cardOpen]: request.status === 'open',
      [styles.cardDone]: request.status === 'done',
      [styles.cardMentioned]: request.mentions_me && request.status !== 'done',
      [styles.cardFresh]: fresh,
    })}>
    <div className={styles.cardTop}>
      <div className={styles.cardTitle}>
        <MentionText text={request.title} members={members} />
        <div className={styles.cardMeta}>
          {TEAM_COPY.FROM(
            request.created_by.name,
            formatTeamStamp(request.created_at, timeZone, nowIso(timeZone)),
          )}
        </div>
        {request.mentions_me && request.status !== 'done' && (
          <div className={styles.mentioned}>{TEAM_COPY.MENTIONED}</div>
        )}
      </div>
      <span className={classNames(styles.chip, CHIP_CLASS[request.status])}>
        {TEAM_COPY.STATUS[request.status]}
      </span>
    </div>
    <Actions request={request} busy={busy} onAction={onAction} />
  </article>
)
